import { Injectable, signal } from '@angular/core';
import { SetupStep } from '../shared/helpers/guided-setup.helper';
import { I18nSipSetupEnum } from '../shared/models/i18n.model';
import { VerificationFamily } from '../shared/helpers/cli-verification-outcome.helper';

export interface VerificationResult {
	family: VerificationFamily;
	/** When the customer may try again (lock or rate limit), as an ISO instant, or null. */
	retryAt: string | null;
	/** Wrong entries still allowed for this number, when the backend said. */
	remainingTotal: number | null;
	/**
	 * False when another number would not help: the refusal was about the account (no SIP trunk, an
	 * impersonated session), not about the number. Absent means it might.
	 */
	numberChangeable?: boolean;
}

/** Which panel is on screen. */
export type SetupPhase =
	| 'intro'
	| 'signIn'
	| 'chooseNumber'
	| 'order'
	/** Before the verification call: the number in full, the declaration if still due, the trigger. */
	| 'verify'
	/** The verification call is out; the code is on screen and the attempt is polled. */
	| 'verifyRunning'
	/** The verification ended without proving the number, or the trigger was refused. */
	| 'verifyResult'
	/** Proven. Stays on the verification step briefly, then continues to the overview by itself. */
	| 'verified'
	/**
	 * The success overview the customer starts the call from - after a booking, a verification, or
	 * both. The name predates the verification step.
	 */
	| 'booked'
	/** The booking came back without a number, or was refused outright. */
	| 'rejected'
	/**
	 * The order timed out or answered HTTP 504: whether it was booked is UNKNOWN. Never shown as
	 * `rejected`, and never offers a retry - a second order could book a second billed account.
	 */
	| 'unconfirmed'
	/**
	 * The order was refused with 422 ADDRESS_INVALID - the contract address could not be used and
	 * nothing was booked. No retry; closing ends the run with the service contact.
	 *
	 * @ticket PS-6605
	 */
	| 'addressInvalid'
	| 'failed'
	/**
	 * The customer asked for the service to contact them after a sign-in problem or a failure. Only
	 * informs; closing it ends the run with the contact callback.
	 */
	| 'serviceFollowUp'
	/** SIP order link only: the offer request is in flight. */
	| 'resolving'
	/** SIP order link only: EXPIRED or INVALID, one screen so a tampered uid reveals nothing more (BR5). */
	| 'linkExpired'
	/** SIP order link only: valid link, but the contract data blocks the order. */
	| 'linkNotOrderable';

/**
 * PS-6495 — the guided setup's state, deliberately OUTSIDE the dialog component.
 *
 * The dialog is the frame of the workflow, and cancelling it must not throw the workflow away: a
 * cancelled dialog simply reopens at the current state (Torsten, 2026-09-04). A component destroyed
 * on close cannot hold that, so the state lives here and the dialog is only a view onto it.
 *
 * `reset()` is therefore called when a run *finishes* or is abandoned outright — never on close.
 */
@Injectable({
	providedIn: 'root',
})
export class GuidedSetupStore {
	readonly phase = signal<SetupPhase>('intro');
	readonly busy = signal(false);
	/** True from the first "Set this up" until the run finishes or is abandoned. */
	readonly active = signal(false);

	/**
	 * The sign-in screen's two recoverable states. Neither is a failure: the customer stays on the
	 * step and tries again, which is the whole point of moving the login into the dialog.
	 *
	 * They live here rather than on the component because cancelling the dialog has to reopen at the
	 * same place. The CREDENTIALS deliberately do not — they stay on the component and die with it,
	 * so a closed dialog leaves no password behind in root-provided state.
	 */
	readonly twoFactorRequired = signal(false);

	/**
	 * Who the customer signed in as, for the header chip. Here and not on the component for the same
	 * reason as the phase: suspending the dialog destroys the component, and a resumed run skips
	 * `enterSignIn()` — the only thing that ever set this — so the chip vanished while the customer
	 * was still very much signed in. It is a name, not a credential: the password stays on the
	 * component and dies with it.
	 */
	readonly signedInAs = signal('');

	/**
	 * Held until the customer closes the failure screen. Emitting it at the moment of failure closed
	 * the dialog out from under them - see `fail()`.
	 */
	readonly failureReason = signal<string | null>(null);

	/**
	 * Where to go once a SECOND sign-in succeeds. Set when an expired token interrupts a run, so the
	 * customer lands back on the screen they were taken off instead of at the start of the flow.
	 */
	readonly resumeAfter = signal<SetupPhase | null>(null);
	/** The i18n key of the sign-in error to show, or null. Resolved in the template. */
	readonly loginError = signal<I18nSipSetupEnum | null>(null);

	/** Consent, screen by screen. Both are required before the order may be placed. */
	readonly termsAccepted = signal(false);
	/**
	 * The § 71 Abs. 3 TKG declaration. REQUIRED (Torsten, 2026-10-09), like the terms box beside it.
	 *
	 * A single checkbox again, replacing the two-statement radio choice of 2026-10-07. The final
	 * wording is self-conditioning — "Sofern mein Unternehmen ... gilt" — so a customer the
	 * provision does not cover waives nothing by ticking it, which is what the discarded first
	 * statement was there to allow.
	 *
	 * DELIBERATELY NOT STORED — PO decision, 2026-10-07 (PS-6561 F3/F4). This and the terms
	 * checkbox are a UI gate and nothing more: the order payload carries `phoneServiceId` and
	 * `startDate`, and neither consent travels with it.
	 *
	 * The consequence, recorded so it is not rediscovered later as a surprise: there is no evidence
	 * that any particular customer accepted the terms or gave this declaration. Asked afterwards,
	 * the honest answer is that we do not know. The declaration is also revocable in text form, and
	 * nothing here can record a revocation. Raised twice and decided; this comment exists so the
	 * next reader takes it as a decision rather than an oversight — and so nobody builds on these
	 * signals expecting them to persist.
	 */
	readonly waiverAccepted = signal(false);

	/** Set on the first press of the confirm button; gates the red highlighting. */
	readonly consentAttempted = signal(false);
	/** The i18n key of the consent error to show, or null. Resolved in the template. */
	readonly consentError = signal<I18nSipSetupEnum | null>(null);

	readonly sipOrdered = signal(false);

	/**
	 * The § 120 TKG declaration that the customer may have the number shown as their own. Unlike the
	 * two order consents its text IS stored: it travels as `declarationText` on the verification
	 * trigger, which keeps it as the evidence. Ticked on the number step, beside the number it is
	 * about (Torsten, 2026-10-14) - and asked again whenever the number is changed.
	 */
	readonly declarationAccepted = signal(false);
	readonly declarationAttempted = signal(false);

	/** The number step's verdict from `number/validate` (or the client pre-check), or null. */
	readonly numberError = signal<I18nSipSetupEnum | null>(null);
	/** The customer already proved the number (validate or trigger said so); no call is placed. */
	readonly numberVerified = signal(false);
	/**
	 * How the last verification ended, for the result screen. Never the code - that lives on the
	 * component-scoped `GuidedSetupVerification` and dies with the dialog.
	 */
	readonly verificationResult = signal<VerificationResult | null>(null);
	readonly manualRequestSent = signal(false);
	/** The manual-verification request could not be sent; the screen says so and the button stays. */
	readonly manualRequestFailed = signal(false);
	/**
	 * The customer chose to go on without proving the number - from the number step after a refusal,
	 * so the run never becomes a dead end (AC7). The SIP number is signalled.
	 */
	readonly verificationSkipped = signal(false);
	readonly outgoingNumber = signal('');

	/**
	 * Picking from the customer's own numbers, or typing a different one. Both were always possible,
	 * but the old field only revealed the candidates on focus, so the choice was invisible until you
	 * happened to click. Making it an explicit mode is what puts both paths on screen.
	 */
	readonly numberMode = signal<'list' | 'custom'>('list');

	/** The SIP number the booking confirmed, shown on the success screen. */
	readonly bookedNumber = signal<string | null>(null);

	/** Resolved from the token and from nothing else (D5). */
	readonly phoneServiceId = signal<number | null>(null);

	/** The list the customer sees, captured once so it cannot renumber mid-flow. */
	readonly displaySteps = signal<SetupStep[]>([]);

	/** SIP order link only: the offer's company name, so a wrong recipient of a forwarded link notices. */
	readonly companyName = signal<string | null>(null);

	reset(): void {
		this.phase.set('intro');
		this.busy.set(false);
		this.active.set(false);
		this.termsAccepted.set(false);
		this.waiverAccepted.set(false);
		this.consentAttempted.set(false);
		this.consentError.set(null);
		this.twoFactorRequired.set(false);
		this.loginError.set(null);
		this.signedInAs.set('');
		// Both were added after this method and are easy to forget here: a stale resume target would
		// send the NEXT run straight to a screen it never reached, and a stale reason would be
		// reported for a failure that did not happen.
		this.resumeAfter.set(null);
		this.failureReason.set(null);
		this.bookedNumber.set(null);
		this.sipOrdered.set(false);
		this.declarationAccepted.set(false);
		this.declarationAttempted.set(false);
		this.numberError.set(null);
		this.numberVerified.set(false);
		this.verificationResult.set(null);
		this.manualRequestSent.set(false);
		this.manualRequestFailed.set(false);
		this.verificationSkipped.set(false);
		this.outgoingNumber.set('');
		this.numberMode.set('list');
		this.phoneServiceId.set(null);
		this.displaySteps.set([]);
		this.companyName.set(null);
	}
}
